import { useCallback } from 'react';
import { useLocation } from 'react-router';
import type { ShopperSearch } from '@/scapi';
import { useCategorySorting } from '@/components/eataly-plp-ordination/use-category-sorting';
import { useConfiguredSorting } from '@/components/eataly-plp-ordination/use-configured-sorting';
import { useNavigate } from '@/hooks/use-navigate';
import {
    applyDefaultSortAndCloseOrdinationPanel,
    applySortAndCloseOrdinationPanel,
    BEST_MATCHES_SORT_ID,
} from '@/lib/plp-panel-url-state';
import { useDataLayer } from '@/hooks/use-data-layer';

type PlpOrdinationResult = {
    sortingOptions?: ShopperSearch.schemas['ProductSearchResult']['sortingOptions'];
};

type UsePlpOrdinationSelectOptions = {
    /** Content folder listings: sort ids from config, labels resolved via i18n. */
    sortingOptionIds?: string[];
};

function getAnalyticsSortType(sortId: string): string {
    if (sortId === 'price-low-to-high') return 'price asc';
    if (sortId === 'price-high-to-low') return 'price desc';
    if (sortId === BEST_MATCHES_SORT_ID) return 'relevance';
    return sortId.replaceAll('-', ' ');
}

export function usePlpOrdinationSelect(result: PlpOrdinationResult, options?: UsePlpOrdinationSelectOptions) {
    const location = useLocation();
    const navigate = useNavigate();
    const dataLayer = useDataLayer();
    const productSorting = useCategorySorting(result);
    const configuredSorting = useConfiguredSorting(options?.sortingOptionIds ?? []);
    const sorting =
        options?.sortingOptionIds && options.sortingOptionIds.length > 0 ? configuredSorting : productSorting;

    const selectSort = useCallback(
        (sortId: string) => {
            dataLayer.track({
                event: 'sort_by_click',
                sort_type: getAnalyticsSortType(sortId),
                event_type: 'ga4_ce',
            });
            // Engine default: omit `sort` so fetchSearchProducts applies `best-matches`.
            if (sortId === BEST_MATCHES_SORT_ID) {
                void navigate(
                    {
                        pathname: location.pathname,
                        search: applyDefaultSortAndCloseOrdinationPanel(location.search),
                    },
                    { replace: true }
                );
                return;
            }
            void navigate(
                {
                    pathname: location.pathname,
                    search: applySortAndCloseOrdinationPanel(location.search, sortId),
                },
                { replace: true }
            );
        },
        [dataLayer, location.pathname, location.search, navigate]
    );

    return {
        ...sorting,
        selectSort,
    };
}
